/**
 * Subtasks List Component
 *
 * Displays a collapsible list of subtasks for a parent task.
 * Loads subtasks immediately on mount to show count badge.
 */

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Loader2, Check, AlertCircle, Plus, Send, ExternalLink, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import type { Task } from '../types';
import { getSubtasks, createTask, reopenTask } from '../api/client';
import { useTaskMutations } from '../hooks/mutations/useTaskMutations';
import { decomposeTask, dismissDecomposeSuggestion, type DecomposeResult } from '../api/ai';
import { useDecomposeSuggestionForTask } from '../hooks/useDecomposeSuggestions';

const priorityBorderColors: Record<number, string> = {
  4: 'border-priority-1',
  3: 'border-priority-2',
  2: 'border-priority-3',
  1: 'border-gray-300 dark:border-gray-600',
};

const priorityFilledColors: Record<number, string> = {
  4: 'border-priority-1 bg-[#db1c42]',
  3: 'border-priority-2 bg-[#d96b15]',
  2: 'border-priority-3 bg-[#fbce57]',
  1: 'border-gray-400 bg-gray-400',
};

interface SubtasksListProps {
  taskId: string;
  /** Whether the parent task is completed (disables adding subtasks) */
  isCompleted?: boolean;
  /** Callback when a subtask is clicked */
  onSelectTask?: (taskId: string) => void;
  /** When true, auto-show the decompose suggestion if one exists */
  showSuggestion?: boolean;
}

export function SubtasksList({ taskId, isCompleted = false, onSelectTask, showSuggestion = false }: SubtasksListProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start loading immediately
  const [subtasks, setSubtasks] = useState<Task[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New subtask state
  const [isAdding, setIsAdding] = useState(false);
  const [newSubtaskContent, setNewSubtaskContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Editing state
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  
  // Task mutations for completion toggle and updates
  const { completeTaskAsync, updateTaskAsync } = useTaskMutations();

  // Decompose state
  const [isDecomposing, setIsDecomposing] = useState(false);
  const [decomposeLoading, setDecomposeLoading] = useState(false);
  const [decomposeResult, setDecomposeResult] = useState<DecomposeResult | null>(null);
  const [decomposeError, setDecomposeError] = useState<string | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isSavingDecompose, setIsSavingDecompose] = useState(false);

  // Pending decompose suggestion from backend (auto-decompose feature)
  const { suggestion: pendingSuggestion } = useDecomposeSuggestionForTask(taskId);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);

  // Auto-show suggestion when it arrives (or when showSuggestion prop is set)
  useEffect(() => {
    // Reset dismissed state when task changes
    setSuggestionDismissed(false);
  }, [taskId]);

  useEffect(() => {
    if (pendingSuggestion && !suggestionDismissed && !isDecomposing && !decomposeResult) {
      // Convert the suggestion into the decompose result format
      setDecomposeResult({
        taskId: pendingSuggestion.taskId,
        task: { id: pendingSuggestion.taskId, content: '' },
        subtasks: pendingSuggestion.suggestions.map((s) => ({
          content: s.content,
          estimatedMinutes: s.estimatedMinutes,
        })),
      });
      setSelectedIndices(new Set(pendingSuggestion.suggestions.map((_, i) => i)));
      setIsDecomposing(true);
      setIsExpanded(true);
    }
  }, [pendingSuggestion, suggestionDismissed, isDecomposing, decomposeResult]);

  // Also auto-expand when showSuggestion prop is set (from URL ?suggest=1)
  useEffect(() => {
    if (showSuggestion && pendingSuggestion && !suggestionDismissed) {
      setIsExpanded(true);
    }
  }, [showSuggestion, pendingSuggestion, suggestionDismissed]);

  // Load subtasks immediately when taskId changes
  useEffect(() => {
    let cancelled = false;

    const loadSubtasks = async () => {
      setIsLoading(true);
      setError(null);
      setIsExpanded(false);
      setIsAdding(false);
      setNewSubtaskContent('');

      try {
        const loadedSubtasks = await getSubtasks(taskId);
        if (!cancelled) {
          setSubtasks(loadedSubtasks || []);
          setHasLoaded(true);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Failed to load subtasks:', err);
          setError(err.message || 'Failed to load subtasks');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSubtasks();

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  /**
   * Toggle expanded state
   */
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * Start adding a new subtask
   */
  const handleStartAdding = () => {
    setIsAdding(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  /**
   * Cancel adding a new subtask
   */
  const handleCancelAdding = () => {
    setIsAdding(false);
    setNewSubtaskContent('');
    setError(null);
  };

  /**
   * Submit a new subtask
   */
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newSubtaskContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const newSubtask = await createTask({
        content: newSubtaskContent.trim(),
        parentId: taskId,
      });
      setSubtasks((prev) => [...prev, newSubtask]);
      setIsExpanded(true);
      setNewSubtaskContent('');
      inputRef.current?.focus();
    } catch (err: any) {
      console.error('Failed to create subtask:', err);
      setError(err.message || 'Failed to create subtask');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleCancelAdding();
    }
  };

  /**
   * Toggle subtask completion status (optimistic update)
   */
  const handleToggleCompletion = async (subtask: Task, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening task details
    
    // Optimistically update UI immediately
    setSubtasks((prev) =>
      prev.map((t) =>
        t.id === subtask.id ? { ...t, isCompleted: !t.isCompleted } : t
      )
    );
    
    try {
      // Make API call in background
      if (subtask.isCompleted) {
        await reopenTask(subtask.id);
      } else {
        await completeTaskAsync({ taskId: subtask.id });
      }
    } catch (err: any) {
      console.error('Failed to toggle subtask completion:', err);
      setError(err.message || 'Failed to update subtask');
      
      // Revert on error
      setSubtasks((prev) =>
        prev.map((t) =>
          t.id === subtask.id ? { ...t, isCompleted: subtask.isCompleted } : t
        )
      );
    }
  };

  /**
   * Start editing a subtask
   */
  const handleStartEditing = (subtask: Task) => {
    if (isCompleted || subtask.isCompleted) return;
    setEditingSubtaskId(subtask.id);
    setEditingContent(subtask.content);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  /**
   * Save edited subtask content
   */
  const handleSaveEdit = async (subtaskId: string) => {
    const trimmed = editingContent.trim();
    if (!trimmed) {
      setEditingSubtaskId(null);
      return;
    }

    const originalSubtask = subtasks?.find(t => t.id === subtaskId);
    if (!originalSubtask || trimmed === originalSubtask.content) {
      setEditingSubtaskId(null);
      return;
    }

    // Optimistically update
    setSubtasks((prev) =>
      prev.map((t) =>
        t.id === subtaskId ? { ...t, content: trimmed } : t
      )
    );
    setEditingSubtaskId(null);

    try {
      await updateTaskAsync({ taskId: subtaskId, updates: { content: trimmed } });
    } catch (err: any) {
      console.error('Failed to update subtask:', err);
      setError(err.message || 'Failed to update subtask');
      // Revert on error
      setSubtasks((prev) =>
        prev.map((t) =>
          t.id === subtaskId ? { ...t, content: originalSubtask.content } : t
        )
      );
    }
  };

  /**
   * Cancel editing
   */
  const handleCancelEdit = () => {
    setEditingSubtaskId(null);
    setEditingContent('');
  };

  /**
   * Start decompose: call API and show confirmation panel
   */
  const handleDecompose = async () => {
    setIsDecomposing(true);
    setIsExpanded(true);
    setIsAdding(false);
    setDecomposeLoading(true);
    setDecomposeResult(null);
    setDecomposeError(null);

    try {
      const result = await decomposeTask(taskId);
      setDecomposeResult(result);
      setSelectedIndices(new Set(result.subtasks.map((_, i) => i)));
    } catch (err: any) {
      setDecomposeError(err.message || 'Failed to decompose task');
    } finally {
      setDecomposeLoading(false);
    }
  };

  const handleCancelDecompose = () => {
    setIsDecomposing(false);
    setDecomposeResult(null);
    setDecomposeError(null);
    setSelectedIndices(new Set());
  };

  const handleToggleDecomposeItem = (index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSaveDecompose = async () => {
    if (!decomposeResult || selectedIndices.size === 0) return;
    setIsSavingDecompose(true);
    const toCreate = decomposeResult.subtasks.filter((_, i) => selectedIndices.has(i));
    const created: Task[] = [];
    for (const suggested of toCreate) {
      try {
        const newSubtask = await createTask({
          content: suggested.content,
          parentId: taskId,
        });
        created.push(newSubtask);
      } catch {
        // continue on individual failure
      }
    }
    // Dismiss the pending suggestion if one exists
    if (pendingSuggestion) {
      dismissDecomposeSuggestion(pendingSuggestion.id).catch(() => {});
      setSuggestionDismissed(true);
    }
    setSubtasks((prev) => [...prev, ...created]);
    setIsSavingDecompose(false);
    handleCancelDecompose();
  };

  const handleDismissSuggestion = async () => {
    if (pendingSuggestion) {
      dismissDecomposeSuggestion(pendingSuggestion.id).catch(() => {});
    }
    setSuggestionDismissed(true);
    handleCancelDecompose();
  };

  /**
   * Handle keyboard events for editing
   */
  const handleEditKeyDown = (e: React.KeyboardEvent, subtaskId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit(subtaskId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div className="space-y-2.5">
      {/* Header - clickable to toggle */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between text-left cursor-pointer group -mx-2 px-2 py-2 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark/50 transition-colors"
      >
        <h4 className="text-sm md:text-[13px] font-medium text-text-tertiary-light dark:text-text-primary-dark0 flex items-center gap-2">
          <span>Subtasks</span>
          {isLoading ? (
            <Loader2 className="w-3 h-3 animate-spin text-text-tertiary-light" />
          ) : (subtasks?.length ?? 0) > 0 ? (
            <span className="text-text-tertiary-light dark:text-text-primary-dark0 font-normal">
              ({subtasks?.length ?? 0})
            </span>
          ) : null}
        </h4>
        <ChevronDown
          className={clsx(
            'w-4 h-4 text-text-tertiary-light dark:text-text-primary-dark0 transition-transform',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Content - expanded section */}
      {isExpanded && (
        <div className="space-y-1">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="flex items-center gap-2 p-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 rounded">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {error}
            </div>
          )}

          {/* Subtasks list */}
          {!isLoading && hasLoaded && (
            <>
              {(subtasks?.length ?? 0) === 0 && !isAdding ? (
                <p className="text-[13px] text-text-tertiary-light dark:text-text-primary-dark0 py-2 text-center">
                  No subtasks
                </p>
              ) : (
                <div className="space-y-1">
                  {subtasks?.map((subtask) => (
                    <div
                      key={subtask.id}
                      className={clsx(
                        'flex items-center gap-2 py-[9px] px-[11px] rounded-md transition-colors group',
                        'bg-surface-base-light dark:bg-surface-hover-dark/50 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark/50',
                        subtask.isCompleted && 'opacity-60'
                      )}
                    >
                      {/* Completion toggle button */}
                      <button
                        onClick={(e) => handleToggleCompletion(subtask, e)}
                        disabled={isCompleted}
                        className={clsx(
                          'shrink-0 w-4 h-4 min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 rounded border-2 flex items-center justify-center transition-all duration-150',
                          subtask.isCompleted
                            ? (priorityFilledColors[subtask.priority ?? 1] ?? priorityFilledColors[1]) + ' cursor-pointer'
                            : (priorityBorderColors[subtask.priority ?? 1] ?? priorityBorderColors[1]) + ' cursor-pointer hover:scale-110',
                          isCompleted && 'cursor-not-allowed opacity-50'
                        )}
                        aria-label={subtask.isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                      >
                        {subtask.isCompleted && (
                          <Check
                            className={clsx('w-2.5 h-2.5', (subtask.priority ?? 1) === 2 ? 'text-yellow-900' : 'text-white')}
                            strokeWidth={3}
                          />
                        )}
                      </button>

                      {/* Content - click to edit inline */}
                      {editingSubtaskId === subtask.id ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          onBlur={() => handleSaveEdit(subtask.id)}
                          onKeyDown={(e) => handleEditKeyDown(e, subtask.id)}
                          className={clsx(
                            'flex-1 text-sm md:text-[13px] py-0.5 px-1 rounded',
                            'bg-surface-raised-light dark:bg-surface-raised-dark border border-primary-300 dark:border-primary-600',
                            'text-text-primary-light dark:text-text-primary-dark',
                            'focus:outline-none focus:ring-1 focus:ring-primary-500'
                          )}
                        />
                      ) : (
                        <div
                          onClick={() => handleStartEditing(subtask)}
                          className={clsx(
                            'flex-1 text-sm md:text-[13px] truncate',
                            subtask.isCompleted
                              ? 'text-text-tertiary-light dark:text-text-primary-dark0 line-through cursor-default'
                              : 'text-text-primary-light dark:text-text-primary-dark cursor-text'
                          )}
                        >
                          {subtask.content}
                        </div>
                      )}

                      {/* Open details button */}
                      <button
                        onClick={() => onSelectTask?.(subtask.id)}
                        className={clsx(
                          'shrink-0 p-2 md:p-1 rounded transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100',
                          'text-text-tertiary-light dark:text-text-secondary-dark hover:text-primary-600 dark:hover:text-primary-400 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark cursor-pointer'
                        )}
                        aria-label="Open task details"
                        title="Open task details"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Decompose confirmation panel */}
              {isDecomposing && (
                <div className="mt-2 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-950/30 p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-[12px] font-medium text-primary-700 dark:text-primary-300">
                    <Sparkles className="w-3.5 h-3.5" />
                    AI suggestions
                  </div>

                  {decomposeLoading && (
                    <div className="flex items-center gap-2 py-2 text-[13px] text-text-tertiary-light dark:text-text-secondary-dark">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-500" />
                      Breaking down task...
                    </div>
                  )}

                  {decomposeError && (
                    <div className="flex items-center gap-2 text-[12px] text-red-600 dark:text-red-400">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      {decomposeError}
                    </div>
                  )}

                  {decomposeResult && !decomposeLoading && (
                    <div className="space-y-1">
                      {decomposeResult.subtasks.map((s, i) => (
                        <label
                          key={i}
                          className="flex items-start gap-2 py-1.5 px-2 rounded-md hover:bg-primary-100/50 dark:hover:bg-primary-900/30 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            checked={selectedIndices.has(i)}
                            onChange={() => handleToggleDecomposeItem(i)}
                            className="mt-0.5 shrink-0 accent-primary-500"
                          />
                          <span className="text-[13px] text-text-primary-light dark:text-text-primary-dark leading-snug">
                            {s.content}
                          </span>
                          {s.estimatedMinutes != null && (
                            <span className="ml-auto shrink-0 text-xs text-text-tertiary-light dark:text-text-tertiary-dark whitespace-nowrap">
                              ~{s.estimatedMinutes}m
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={handleSaveDecompose}
                      disabled={isSavingDecompose || decomposeLoading || selectedIndices.size === 0}
                      className={clsx(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors',
                        'bg-primary-700 text-white hover:bg-primary-800',
                        'disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer'
                      )}
                    >
                      {isSavingDecompose ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Plus className="w-3.5 h-3.5" />
                      )}
                      Add {selectedIndices.size > 0 ? selectedIndices.size : ''} subtask{selectedIndices.size !== 1 ? 's' : ''}
                    </button>
                    <button
                      onClick={handleCancelDecompose}
                      disabled={isSavingDecompose}
                      className="px-2 py-1.5 text-[13px] text-text-tertiary-light hover:text-text-secondary-light dark:hover:text-text-secondary-dark cursor-pointer"
                    >
                      Cancel
                    </button>
                    {pendingSuggestion && !suggestionDismissed && (
                      <button
                        onClick={handleDismissSuggestion}
                        disabled={isSavingDecompose}
                        className="px-2 py-1.5 text-[13px] text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 cursor-pointer"
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Add subtask form */}
              {!isCompleted && !isDecomposing && (
                <div className="mt-1">
                  {isAdding ? (
                    <form onSubmit={handleSubmit} className="flex gap-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={newSubtaskContent}
                        onChange={(e) => setNewSubtaskContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="New subtask..."
                        disabled={isSubmitting}
                        className={clsx(
                          'flex-1 py-3 md:py-[9px] px-3 md:px-[11px] text-sm md:text-[13px] rounded-md',
                          'bg-surface-base-light dark:bg-surface-hover-dark/50 border border-border-subtle-light dark:border-border-subtle-dark',
                          'text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-secondary-dark',
                          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                          'disabled:opacity-50'
                        )}
                      />
                      <button
                        type="submit"
                        disabled={!newSubtaskContent.trim() || isSubmitting}
                        className={clsx(
                          'px-3 py-2 rounded-md transition-colors',
                          'bg-primary-700 text-white hover:bg-primary-800',
                          'disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer',
                          'flex items-center justify-center'
                        )}
                        title="Add subtask (Enter)"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelAdding}
                        disabled={isSubmitting}
                        className="px-2 py-2 text-[13px] text-text-tertiary-light hover:text-text-secondary-light dark:hover:text-text-secondary-dark cursor-pointer"
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={handleStartAdding}
                        className="flex items-center gap-1.5 py-[9px] px-[11px] text-[13px] text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950 rounded-md transition-colors cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add subtask
                      </button>
                      <button
                        onClick={handleDecompose}
                        className="flex items-center gap-1.5 py-[9px] px-[11px] text-[13px] text-text-tertiary-light dark:text-text-secondary-dark hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-950 rounded-md transition-colors cursor-pointer"
                        title="Use AI to suggest subtasks"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Break down
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
